import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import ThemeContext from '@hig/theme-context';
import { createCustomClassNames } from '@hig/utils';
import { ControlBehavior } from '@hig/behaviors';

var surfaces = Object.freeze({
  SURFACELEVEL100: 100,
  SURFACELEVEL200: 200,
  SURFACELEVEL250: 250,
  SURFACELEVEL300: 300,
  SURFACELEVEL350: 350
});

var AVAILABLE_SURFACES = Object.freeze(Object.values(surfaces));

var variants = Object.freeze({
  DYNAMIC: "dynamic",
  STATIC: "static"
});

var AVAILABLE_VARIANTS = Object.freeze(Object.values(variants));

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function getDefaultIconTransitionProperties() {
  return {
    transitionDuration: "0.3s",
    transitionProperty: "fill"
  };
}

function getStylesByHover(themeData, surface, on, variant) {
  var surfaceLevel = Number(surface);
  var isDynamic = variant === "dynamic";
  var dynamicHoverIconColor = on ? themeData["iconButton.dynamic.on.hover.iconColor"] : themeData["iconButton.dynamic.hover.iconColor"];
  var hoverIconColor = isDynamic ? dynamicHoverIconColor : "";
  var staticSurfaceLevelBackgroundColor = surfaceLevel <= 250 ? themeData["iconButton.static.hover.100To250BackgroundColor"] : themeData["iconButton.static.hover.300To350BackgroundColor"];
  var staticHoverBackgroundColor = on ? themeData["iconButton.static.on.hover.100To250BackgroundColor"] : staticSurfaceLevelBackgroundColor;
  var backgroundColor = !isDynamic ? staticHoverBackgroundColor : "transparent";
  var staticBorderColor = on ? themeData["iconButton.static.on.hover.borderColor"] : "transparent";
  var borderColor = !isDynamic ? staticBorderColor : "transparent";

  return {
    backgroundColor: backgroundColor,
    borderColor: borderColor,
    boxShadow: "none",
    "& svg *": _extends({}, getDefaultIconTransitionProperties(), {
      fill: hoverIconColor
    })
  };
}

function getStylesByFocus(themeData, on, variant) {
  var isDynamic = variant === "dynamic";
  var dynamicFocusIconColor = on ? themeData["iconButton.dynamic.on.focus.iconColor"] : themeData["iconButton.dynamic.focus.iconColor"];
  var focusIconColor = isDynamic ? dynamicFocusIconColor : "";
  var staticBorderColor = on ? themeData["iconButton.static.on.focus.borderColor"] : "transparent";
  var borderColor = !isDynamic ? staticBorderColor : "transparent";

  return {
    borderColor: borderColor,
    boxShadow: "0 0 0 " + themeData["iconButton.focus.haloWidth"] + " " + themeData["iconButton.focus.haloColor"],
    transitionDuration: "0.3s, 0.3s",
    "& svg *": _extends({}, getDefaultIconTransitionProperties(), {
      fill: focusIconColor
    })
  };
}

function getStylesByPressed(themeData, surface, on, variant) {
  var surfaceLevel = Number(surface);
  var dynamicPressedIconColor = on ? themeData["iconButton.dynamic.on.pressed.iconColor"] : themeData["iconButton.dynamic.pressed.iconColor"];
  var pressedIconColor = variant === "dynamic" ? dynamicPressedIconColor : "";

  var dynamicPressedBackgroundColor = surfaceLevel <= 250 ? themeData["iconButton.dynamic.pressed.100To250BackgroundColor"] : themeData["iconButton.dynamic.pressed.300To350BackgroundColor"];
  var staticPressedBackgroundColor = themeData["iconButton.static.on.pressed.backgroundColor"];
  return {
    backgroundColor: variant === "dynamic" ? dynamicPressedBackgroundColor : staticPressedBackgroundColor,
    borderColor: variant === "dynamic" ? "transparent" : themeData["iconButton.static.on.pressed.borderColor"],
    transitionDuration: "0.3s, 0.3s",
    "& svg *": _extends({}, getDefaultIconTransitionProperties(), {
      fill: pressedIconColor
    })
  };
}

function getStylesByDisabled(themeData) {
  return {
    opacity: themeData["colorScheme.opacity.disabled"],
    pointerEvents: "none"
  };
}

function stylesheet(props, themeData, density) {
  var disabled = props.disabled,
      hasFocus = props.hasFocus,
      hasHover = props.hasHover,
      isPressed = props.isPressed,
      on = props.on,
      customStylesheet = props.stylesheet,
      surface = props.surface,
      variant = props.variant;

  var contentHeight = density === "medium-density" ? "20px" : "16px";
  var isDynamic = variant === "dynamic";
  var dynamicIconColor = on ? themeData["iconButton.dynamic.on.default.iconColor"] : themeData["iconButton.dynamic.default.iconColor"];
  var iconColor = isDynamic ? dynamicIconColor : "";

  var styles = {
    iconButton: _extends({
      backgroundColor: !isDynamic && on ? themeData["iconButton.static.on.default.backgroundColor"] : "transparent",
      borderColor: !isDynamic && on ? themeData["iconButton.static.on.default.borderColor"] : "transparent",
      borderStyle: "solid",
      borderWidth: "1px",
      display: "inline-block",
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
      borderRadius: themeData["iconButton.borderRadius"],
      padding: "0",
      height: "calc(" + contentHeight + " + (" + themeData["density.spacings.extraSmall"] + " * 2))",
      lineHeight: "calc(" + contentHeight + " + (" + themeData["density.spacings.extraSmall"] + " * 2))",
      width: "calc(" + contentHeight + " + (" + themeData["density.spacings.extraSmall"] + " * 2))",
      outline: 0,
      transitionProperty: "box-shadow, background-color",
      transitionDuration: "0.3s, 0.3s",
      transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1), cubic-bezier(0.4, 0, 0.2, 1)",
      "& svg *": {
        fill: iconColor,
        transitionDuration: "0.3s",
        transitionProperty: "fill"
      }
    }, hasFocus ? getStylesByFocus(themeData, on, variant) : {}, hasHover ? getStylesByHover(themeData, surface, on, variant) : {}, isPressed ? getStylesByPressed(themeData, surface, on, variant) : {}, disabled ? getStylesByDisabled(themeData) : {}),
    iconSpacer: {
      width: "24px"
    },
    iconButtonIcon: {
      position: "absolute",
      left: 0,
      right: 0,
      margin: "0 auto",
      top: "50%",
      transform: "translateY(-50%)",
      pointerEvents: "none"
    }
  };

  return customStylesheet ? customStylesheet(styles, props, themeData, density) : styles;
}

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var IconButtonPresenter = function (_Component) {
  _inherits(IconButtonPresenter, _Component);

  function IconButtonPresenter() {
    _classCallCheck(this, IconButtonPresenter);

    return _possibleConstructorReturn(this, (IconButtonPresenter.__proto__ || Object.getPrototypeOf(IconButtonPresenter)).apply(this, arguments));
  }

  _createClass(IconButtonPresenter, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _props = this.props,
          className = _props.className,
          disabled = _props.disabled,
          hasFocus = _props.hasFocus,
          hasHover = _props.hasHover,
          higIcon = _props.icon,
          isPressed = _props.isPressed,
          link = _props.link,
          on = _props.on,
          onClick = _props.onClick,
          onBlur = _props.onBlur,
          onFocus = _props.onFocus,
          onMouseDown = _props.onMouseDown,
          onMouseEnter = _props.onMouseEnter,
          onMouseLeave = _props.onMouseLeave,
          onMouseUp = _props.onMouseUp,
          customStylesheet = _props.stylesheet,
          surface = _props.surface,
          title = _props.title,
          variant = _props.variant,
          otherProps = _objectWithoutProperties(_props, ["className", "disabled", "hasFocus", "hasHover", "icon", "isPressed", "link", "on", "onClick", "onBlur", "onFocus", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseUp", "stylesheet", "surface", "title", "variant"]);

      return React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref) {
          var resolvedRoles = _ref.resolvedRoles,
              metadata = _ref.metadata;

          var linkProps = link ? {
            tabIndex: disabled ? "-1" : "0",
            href: link
          } : {};
          var props = _extends$1({}, linkProps, {
            disabled: disabled,
            hasFocus: hasFocus,
            hasHover: hasHover,
            higIcon: higIcon,
            isPressed: isPressed,
            on: on,
            onClick: onClick,
            onBlur: onBlur,
            onFocus: onFocus,
            onMouseDown: onMouseDown,
            onMouseEnter: onMouseEnter,
            onMouseLeave: onMouseLeave,
            onMouseUp: onMouseUp,
            stylesheet: customStylesheet,
            surface: surface,
            title: title,
            variant: variant
          });
          var iconButtonIconClassName = createCustomClassNames(className, "icon-button-icon");
          var Element = _this2.props.link ? "a" : "button";
          var styles = stylesheet(props, resolvedRoles, metadata.densityId);
          var icon = React.cloneElement(higIcon, {
            className: cx(css(styles.iconButtonIcon), iconButtonIconClassName)
          });
          var tabIndex = disabled ? "-1" : otherProps.tabIndex || "0";

          return React.createElement(
            Element,
            _extends$1({}, otherProps, {
              className: cx(css(styles.iconButton), className),
              disabled: disabled,
              onClick: onClick,
              onBlur: onBlur,
              onFocus: onFocus,
              onMouseDown: onMouseDown,
              onMouseEnter: onMouseEnter,
              onMouseLeave: onMouseLeave,
              onMouseUp: onMouseUp,
              tabIndex: tabIndex,
              title: title
            }, linkProps),
            icon
          );
        }
      );
    }
  }]);

  return IconButtonPresenter;
}(Component);

IconButtonPresenter.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  hasFocus: PropTypes.bool,
  hasHover: PropTypes.bool,
  icon: PropTypes.element,
  isPressed: PropTypes.bool,
  link: PropTypes.string,
  on: PropTypes.bool,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseUp: PropTypes.func,
  stylesheet: PropTypes.func,
  surface: PropTypes.oneOf(AVAILABLE_SURFACES),
  title: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(AVAILABLE_VARIANTS)
};
IconButtonPresenter.__docgenInfo = {
  "description": "",
  "displayName": "IconButtonPresenter",
  "props": {
    "className": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasFocus": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "icon": {
      "type": {
        "name": "element"
      },
      "required": false,
      "description": ""
    },
    "isPressed": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "link": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "on": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseUp": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "surface": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_SURFACES"
      },
      "required": false,
      "description": ""
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": true,
      "description": ""
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_VARIANTS"
      },
      "required": false,
      "description": ""
    }
  }
};

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var IconButton = function (_Component) {
  _inherits$1(IconButton, _Component);

  function IconButton() {
    _classCallCheck$1(this, IconButton);

    return _possibleConstructorReturn$1(this, (IconButton.__proto__ || Object.getPrototypeOf(IconButton)).apply(this, arguments));
  }

  _createClass$1(IconButton, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          disabled = _props.disabled,
          onBlur = _props.onBlur,
          onFocus = _props.onFocus,
          onMouseDown = _props.onMouseDown,
          onMouseEnter = _props.onMouseEnter,
          onMouseLeave = _props.onMouseLeave,
          onMouseUp = _props.onMouseUp,
          stylesheet = _props.stylesheet,
          props = _objectWithoutProperties$1(_props, ["disabled", "onBlur", "onFocus", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseUp", "stylesheet"]);

      return React.createElement(
        ControlBehavior,
        {
          onBlur: onBlur,
          onFocus: onFocus,
          onMouseDown: onMouseDown,
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          onMouseUp: onMouseUp
        },
        function (_ref) {
          var hasFocus = _ref.hasFocus,
              hasHover = _ref.hasHover,
              isPressed = _ref.isPressed,
              handleBlur = _ref.onBlur,
              handleFocus = _ref.onFocus,
              handleMouseDown = _ref.onMouseDown,
              handleMouseEnter = _ref.onMouseEnter,
              handleMouseLeave = _ref.onMouseLeave,
              handleMouseUp = _ref.onMouseUp;
          return React.createElement(IconButtonPresenter, _extends$2({
            disabled: disabled,
            hasFocus: hasFocus && !disabled,
            hasHover: hasHover && !disabled,
            isPressed: isPressed && !disabled,
            onBlur: handleBlur,
            onFocus: handleFocus,
            onMouseDown: handleMouseDown,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onMouseUp: handleMouseUp,
            stylesheet: stylesheet
          }, props));
        }
      );
    }
  }]);

  return IconButton;
}(Component);

IconButton.propTypes = {
  /**
   * Prevents user actions on the button
   */
  disabled: PropTypes.bool,
  /**
   * Icon or svg to be rendered
   */
  icon: PropTypes.element,
  /**
   * Url button will navigate to when clicked
   */
  link: PropTypes.string,
  /**
   * The icon-button is toggled to on when this is set
   */
  on: PropTypes.bool,
  /**
   * Called when user moves focus away from the button
   */
  onBlur: PropTypes.func,
  /**
   * Called when user clicks the button
   */
  onClick: PropTypes.func,
  /**
   * Called when user moves focus onto the button
   */
  onFocus: PropTypes.func,
  /**
   * Called when mouse is pressed over the button
   */
  onMouseDown: PropTypes.func,
  /**
   * Called when mouse begins to move over the button
   */
  onMouseEnter: PropTypes.func,
  /**
   * Called when mouse stops moving over the button
   */
  onMouseLeave: PropTypes.func,
  /**
   * Called when mouse is released over the button
   */
  onMouseUp: PropTypes.func,
  /**
   * Function to modify the component's styles
   */
  stylesheet: PropTypes.func,
  /**
   * Surface color level that the icon-button will be sitting on
   */
  surface: PropTypes.oneOf(AVAILABLE_SURFACES),
  /**
   * Title of the button for accessibility purposes
   */
  title: PropTypes.string,
  /**
   * The visual variant of the icon-button
   */
  variant: PropTypes.oneOf(AVAILABLE_VARIANTS)
};
IconButton.defaultProps = {
  surface: surfaces.SURFACELEVEL100,
  variant: variants.DYNAMIC
};
IconButton.__docgenInfo = {
  "description": "",
  "displayName": "IconButton",
  "props": {
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Prevents user actions on the button"
    },
    "icon": {
      "type": {
        "name": "element"
      },
      "required": false,
      "description": "Icon or svg to be rendered"
    },
    "link": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Url button will navigate to when clicked"
    },
    "on": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "The icon-button is toggled to on when this is set"
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user moves focus away from the button"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user clicks the button"
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user moves focus onto the button"
    },
    "onMouseDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse is pressed over the button"
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse begins to move over the button"
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse stops moving over the button"
    },
    "onMouseUp": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse is released over the button"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    },
    "surface": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_SURFACES"
      },
      "required": false,
      "description": "Surface color level that the icon-button will be sitting on",
      "defaultValue": {
        "value": "surfaces.SURFACELEVEL100",
        "computed": true
      }
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Title of the button for accessibility purposes"
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_VARIANTS"
      },
      "required": false,
      "description": "The visual variant of the icon-button",
      "defaultValue": {
        "value": "variants.DYNAMIC",
        "computed": true
      }
    }
  }
};

export default IconButton;
export { surfaces, AVAILABLE_SURFACES, variants, AVAILABLE_VARIANTS };
